import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; // ✅ ADD THIS

// Import components
import AdminHeader from './components/AdminHeader/AdminHeader';
import AdminSidebar from './components/AdminSidebar/AdminSidebar';
import SupportTicketModal from './components/SupportTicketModal/SupportTicketModal';

// Import admin page components
import AdminDashboard from './pages/AdminDashboard/AdminDashboard';
import UserManagement from './pages/UserManagement/UserManagement';
import DepositRequests from './pages/DepositRequests/DepositRequests';
import WithdrawalRequests from './pages/WithdrawalRequests/WithdrawalRequests';
import TransactionHistory from './pages/TransactionHistory/TransactionHistory';
import TradingAccounts from './pages/TradingAccounts/TradingAccounts';
import SystemSettings from './pages/SystemSettings/SystemSettings';
import UpiSettings from './pages/UpiSettings/UpiSettings';
import BankSettings from './pages/BankSettings/BankSettings';
import CryptoSettings from './pages/CryptoSettings/CryptoSettings';

import styles from './AdminPortal.module.css';

function AdminPortal() {
  // ✅ ADD: Auth context and session protection
  const { user, logout } = useAuth();
  
  // ✅ ADD: Prevent state changes after unmount
  const isMountedRef = useRef(true);
  
  // ✅ ADD: Session stability tracking
  const sessionStableRef = useRef(true);
  const lastUserRef = useRef(null);
  
  // Existing state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  // ✅ ADD: Cleanup function
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ✅ ADD: Session validation and protection
  useEffect(() => {
    // Early return if component is unmounted
    if (!isMountedRef.current) return;

    // Check if user session is valid
    if (!user) {
      console.warn('⚠️ AdminPortal: No user session found');
      return;
    }

    // Check if user is admin
    if (!user.isAdmin && user.role !== 'admin') {
      console.warn('⚠️ AdminPortal: User is not admin, redirecting');
      logout();
      return;
    }

    // ✅ PREVENT REFRESH: Detect user session changes
    if (lastUserRef.current && lastUserRef.current.id !== user.id) {
      console.warn('⚠️ AdminPortal: User session changed unexpectedly');
      sessionStableRef.current = false;
      return;
    }

    // Store user reference
    lastUserRef.current = user;
    sessionStableRef.current = true;

  }, [user, logout]);

  // ✅ ADD: Stable callback functions
  const handleSupportTicketClick = useCallback(() => {
    if (!isMountedRef.current) return;
    setSupportModalOpen(true);
  }, []);

  const handleSidebarToggle = useCallback(() => {
    if (!isMountedRef.current) return;
    setSidebarCollapsed(prev => !prev);
  }, []);

  const handleSupportModalClose = useCallback(() => {
    if (!isMountedRef.current) return;
    setSupportModalOpen(false);
  }, []);

  // ✅ ADD: Prevent render if session is unstable
  if (!sessionStableRef.current) {
    console.log('🔄 AdminPortal: Waiting for stable session...');
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading admin panel...</p>
      </div>
    );
  }

  // ✅ ADD: Early return if no user or not admin
  if (!user || (!user.isAdmin && user.role !== 'admin')) {
    console.log('❌ AdminPortal: Invalid user session');
    return <Navigate to="/login" replace />;
  }

  return (
    <div className={styles.adminContainer}>
      <AdminSidebar 
        collapsed={sidebarCollapsed} 
        onToggle={handleSidebarToggle}
      />
      
      <div className={`${styles.mainContent} ${sidebarCollapsed ? styles.expanded : ''}`}>
        <AdminHeader 
          onSupportTicketClick={handleSupportTicketClick}
        />
        
        <main className={styles.content}>
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="deposits" element={<DepositRequests />} />
            <Route path="withdrawals" element={<WithdrawalRequests />} />
            <Route path="transactions" element={<TransactionHistory />} />
            <Route path="trading-accounts" element={<TradingAccounts />} />
            <Route path="settings" element={<SystemSettings />} />
<Route path="upi-settings" element={<UpiSettings />} />
<Route path="bank-settings" element={<BankSettings />} />
<Route path="crypto-settings" element={<CryptoSettings />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>

      {/* Support Ticket Modal */}
      <SupportTicketModal 
        isOpen={supportModalOpen} 
        onClose={handleSupportModalClose}
      />
    </div>
  );
}

export default AdminPortal;
